import React, { forwardRef, useImperativeHandle, useCallback } from "react";
import { JSONContent, useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { FiBold, FiItalic, FiUnderline, FiZap, FiFileText } from "react-icons/fi";

// Type-safe icon wrappers
const BoldIcon = FiBold as React.FC<React.SVGProps<SVGSVGElement>>;
const ItalicIcon = FiItalic as React.FC<React.SVGProps<SVGSVGElement>>;
const UnderlineIcon = FiUnderline as React.FC<React.SVGProps<SVGSVGElement>>;
const ZapIcon = FiZap as React.FC<React.SVGProps<SVGSVGElement>>;
const FileTextIcon = FiFileText as React.FC<React.SVGProps<SVGSVGElement>>;

interface ComposeEditorProps {
  subject: string;
  onSubjectChange: (value: string) => void;
  content: JSONContent | null;
  onContentChange: (content: JSONContent) => void;
  onAIRevamp: () => void;
  onUseTemplate: () => void;
}

export interface ComposeEditorRef {
  insertAtCursor: (text: string) => void;
  setContent: (html: string) => void;
  getHTML: () => string;
  getText: () => string;
}

/**
 * ComposeEditor - Returns TWO separate BLACK brutalist sections:
 * 1. Subject field section
 * 2. TipTap editor section with toolbar
 */
const ComposeEditor = forwardRef<ComposeEditorRef, ComposeEditorProps>(
  ({ subject, onSubjectChange, onContentChange, onAIRevamp, onUseTemplate }, ref) => {
    // Initialize TipTap editor
    const editor = useEditor({
      extensions: [
        StarterKit,
        Underline,
      ],
      content: "",
      editorProps: {
        attributes: {
          class: "su-compose-editor-content",
        },
      },
      onUpdate: ({ editor }) => {
        onContentChange(editor.getJSON());
      },
    });

    // Expose editor methods to parent
    useImperativeHandle(ref, () => ({
      insertAtCursor: (text: string) => {
        if (editor) {
          editor.chain().focus().insertContent(text).run();
        }
      },
      setContent: (html: string) => {
        if (editor) {
          editor.commands.setContent(html);
        }
      },
      getHTML: () => {
        if (editor) {
          return editor.getHTML();
        }
        return "";
      },
      getText: () => {
        if (editor) {
          return editor.getText();
        }
        return "";
      },
    }));

    const toggleBold = useCallback(() => {
      editor?.chain().focus().toggleBold().run();
    }, [editor]);

    const toggleItalic = useCallback(() => {
      editor?.chain().focus().toggleItalic().run();
    }, [editor]);

    const toggleUnderline = useCallback(() => {
      editor?.chain().focus().toggleUnderline().run();
    }, [editor]);

    return (
      <>
        {/* Subject - Sits directly on page (no section wrapper) */}
        <div className="su-compose-subject">
          <label className="su-compose-label">SUBJECT</label>
          <input
            type="text"
            className="su-compose-input"
            placeholder="Email subject..."
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
          />
        </div>

        {/* Editor Section - Own BLACK brutalist card */}
        <div className="su-section su-compose-editor-section">
          {/* Toolbar */}
          <div className="su-compose-toolbar">
            <div className="su-compose-toolbar-group">
              {/* Format buttons */}
              <button
                type="button"
                className={`su-compose-format-btn ${editor?.isActive("bold") ? "active" : ""}`}
                onClick={toggleBold}
                title="Bold"
              >
                <BoldIcon />
              </button>
              <button
                type="button"
                className={`su-compose-format-btn ${editor?.isActive("italic") ? "active" : ""}`}
                onClick={toggleItalic}
                title="Italic"
              >
                <ItalicIcon />
              </button>
              <button
                type="button"
                className={`su-compose-format-btn ${editor?.isActive("underline") ? "active" : ""}`}
                onClick={toggleUnderline}
                title="Underline"
              >
                <UnderlineIcon />
              </button>

              {/* Divider */}
              <div className="su-compose-toolbar-divider" />

              {/* AI Revamp button */}
              <button
                type="button"
                className="su-compose-ai-btn"
                onClick={onAIRevamp}
              >
                <ZapIcon />
                <span>AI REVAMP</span>
              </button>

              {/* Use Template button */}
              <button
                type="button"
                className="su-compose-template-btn"
                onClick={onUseTemplate}
              >
                <FileTextIcon />
                <span>USE TEMPLATE</span>
              </button>
            </div>
          </div>

          {/* Editor Content */}
          <div className="su-compose-editor-area">
            <EditorContent editor={editor} />
            {!editor?.getText() && (
              <div className="su-compose-editor-placeholder">
                Write your update here...
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
);

ComposeEditor.displayName = "ComposeEditor";

export default ComposeEditor;
